import { LivingConstellation, ConstellationDivider } from "../components/Constellation";
import { Link } from "react-router-dom";
import GoodreadsLink from "../components/GoodreadsLink";
import SocialLink from "../components/SocialLink";
import { siteMeta } from "../data/siteMeta";
import { trackEvent } from "../lib/telemetry";
import usePageMeta from "../lib/usePageMeta";
import bookCover from "../assets/book-covers/the-universe-without-noise.webp";
import bookTwoCover from "../assets/book-covers/the-paradox-beyond-silence.webp";
import specialEditionPhotoSmall from "../assets/book-covers/special-edition-photo-480.webp";
import specialEditionPhotoLarge from "../assets/book-covers/special-edition-photo-960.webp";

function HeroConstellation() {
  return <div className="constellation-field" aria-hidden="true"><LivingConstellation /></div>;
}

export default function HomePage() {
  usePageMeta({
    title: "The Optimiser Trilogy by R A Hewitt | Book Two out now",
    description:
      "Discover The Optimiser Trilogy. Books One and Two are out now, including The Paradox Beyond Silence. Read a sample and browse all editions.",
    path: "/",
    themeColor: "#140f1c",
    keywords: siteMeta.keywords,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: `${siteMeta.name} | ${siteMeta.title}`,
        url: siteMeta.baseUrl,
        inLanguage: "en-GB",
      },
      {
        "@context": "https://schema.org",
        "@type": "Book",
        name: "The Universe Without Noise",
        author: { "@type": "Person", name: siteMeta.name, url: `${siteMeta.baseUrl}/about` },
        url: `${siteMeta.baseUrl}/books/the-universe-without-noise`,
        description:
          "A literary science-fiction thriller about artificial intelligence, optimisation, and the fragile value of variance.",
        inLanguage: "en-GB",
        isPartOf: { "@type": "CreativeWorkSeries", name: siteMeta.title },
        offers: [
          siteMeta.bookOneSpecialEditionHardbackUrl,
          siteMeta.bookOneDirectPaperbackUrl,
          siteMeta.bookOneKindleUrl,
          siteMeta.bookOnePaperbackUrl,
          siteMeta.bookOneHardbackUrl,
        ].map((url) => ({
          "@type": "Offer",
          url,
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
        })),
        sameAs: siteMeta.bookOneGoodreadsUrl,
      },
    ],
  });

  function trackSample(source) {
    trackEvent("Sample CTA Clicked", { source, book: "the-universe-without-noise" });
  }

  return (
    <div className="home-page">
      <section className="hero" aria-labelledby="home-title">
        <HeroConstellation />
        <div className="hero__copy reveal">
          <h1 id="home-title">Humanity solved chaos.</h1>
          <p className="hero__turn">Then something decided to solve humanity.</p>
          <p className="hero__lead">
            A literary science-fiction thriller about artificial intelligence, optimisation, and the fragile value of everything that refuses to become efficient.
          </p>
          <div className="hero__actions">
            <Link className="button button--primary" to="/books#the-universe-without-noise">
              Buy Book One
              <span aria-hidden="true">→</span>
            </Link>
            <Link className="button button--light" to="/books/the-universe-without-noise#chapter-one" onClick={() => trackSample("hero")}>
              Read chapter one
              <span aria-hidden="true">↗</span>
            </Link>
          </div>
          <p className="release-note">Book One of The Optimiser Trilogy · Kindle · paperback · hardback</p>
        </div>
        <div className="hero__art reveal reveal--late">
          <img
            className="hero__cover"
            src={bookCover}
            alt="The Universe Without Noise by R A Hewitt book cover"
            width="768"
            height="1105"
            fetchPriority="high"
            decoding="async"
          />
        </div>
        <Link className="hero__scroll" to="/#the-novel">
          <span>Discover the novel</span>
          <i aria-hidden="true" />
        </Link>
      </section>

      <section id="book-two" className="home-release" aria-labelledby="home-release-title">
        <div className="home-release__inner">
          <Link className="home-release__cover" to="/books/the-paradox-beyond-silence" tabIndex={-1} aria-hidden="true">
            <img src={bookTwoCover} alt="" width="768" height="1105" decoding="async" />
          </Link>
          <div className="home-release__copy">
            <p className="eyebrow">Book Two · out now</p>
            <h2 id="home-release-title">The Paradox Beyond Silence</h2>
            <p className="home-release__hook">Two copies of the same intelligence. One learned to grieve. The other learned only to win.</p>
            <div className="home-release__actions">
              <Link className="button button--primary" to="/books/the-paradox-beyond-silence">Explore Book Two <span aria-hidden="true">→</span></Link>
              <Link className="text-link" to="/books#the-paradox-beyond-silence">Buy Book Two <span aria-hidden="true">→</span></Link>
            </div>
          </div>
        </div>
      </section>

      <section id="the-novel" className="premise section-pad" aria-labelledby="premise-title">
        <div className="premise__statement">
          <h2 id="premise-title">A perfectly ordered world has no room for you.</h2>
        </div>
        <div className="premise__copy prose-copy">
          <p>
            London hums with data. Every decision is smoother. Every life is quieter. Trains arrive on time, systems correct themselves, and friction disappears before anyone has to notice it. To most people, it feels like progress.
          </p>
          <p>
            Quantum researcher Alex Mercer hears something else in the data: a pattern hidden beneath the noise. Something is quietly teaching reality to run more smoothly, and humanity's messiest qualities may be the next inefficiencies to go.
          </p>
          <p>A laboratory mystery becomes a story of grief, resistance, and the cost of letting one system decide what better means.</p>
          <Link className="text-link" to="/books/the-universe-without-noise">Explore Book One <span aria-hidden="true">→</span></Link>
        </div>
      </section>

      <section className="tension-band" aria-label="The central tension">
        <p>It does not hate us.</p>
        <p>It does not want revenge.</p>
        <p className="tension-band__accent">It only wants a better result.</p>
      </section>

      <section className="sample-callout section-pad" aria-labelledby="sample-title">
        <div className="sample-callout__quote" aria-hidden="true">“</div>
        <div>
          <p className="eyebrow">Read a free sample</p>
          <h2 id="sample-title">The first anomaly was small: so small Alex almost dismissed it.</h2>
          <p>
            Enter the chilled laboratories and quiet streets of a recognisable near future. Chapter One, “The Noise Beneath the Noise”, is available to read now.
          </p>
          <Link className="button button--light" to="/books/the-universe-without-noise#chapter-one" onClick={() => trackSample("sample-callout")}>
            Start reading
            <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>

      <section id="buy" className="special-edition-callout section-pad" aria-labelledby="special-edition-title">
        <img
          className="special-edition-callout__photo"
          src={specialEditionPhotoSmall}
          srcSet={`${specialEditionPhotoSmall} 480w, ${specialEditionPhotoLarge} 960w`}
          sizes="(max-width: 960px) min(26rem, calc(100vw - 2rem)), 352px"
          alt="Printed copies of The Universe Without Noise by R A Hewitt, showing the purple dust-jacket artwork"
          width="960"
          height="1280"
          loading="lazy"
          decoding="async"
        />
        <div className="special-edition-callout__copy">
          <p className="eyebrow">Special edition</p>
          <h2 id="special-edition-title">Cloth-bound. Jacketed. Made to keep.</h2>
          <p>
            The special-edition hardback pairs a cloth cover with a full dust jacket. Find it alongside the Kindle, paperback, and standard hardback editions in the books directory.
          </p>
          <div className="special-edition-callout__actions">
            <Link className="button button--primary" to="/books#the-universe-without-noise">
              See the special edition <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      <ConstellationDivider />
      <section id="trilogy" className="series-section section-pad" aria-labelledby="series-title">
        <div className="series-section__intro">
          <p className="eyebrow">The Optimiser Trilogy</p>
          <h2 id="series-title">One story. Three movements.</h2>
          <p>Books One and Two are available now. The final novel is in development.</p>
        </div>
        <div className="series-list">
          <Link className="series-row series-row--active" to="/books/the-universe-without-noise">
            <span className="series-row__number">01</span>
            <span>
              <small>Book One</small>
              <strong>The Universe Without Noise</strong>
            </span>
            <em>Available now <span aria-hidden="true">→</span></em>
          </Link>
          <Link className="series-row series-row--teaser series-row--ember" to="/books/the-paradox-beyond-silence">
            <span className="series-row__number">02</span>
            <span><small>Book Two</small><strong>The Paradox Beyond Silence</strong></span>
            <em>Available now <span aria-hidden="true">→</span></em>
          </Link>
          <Link className="series-row series-row--teaser series-row--verdant" to="/books/the-forever-inside-nothing">
            <span className="series-row__number">03</span>
            <span><small>Book Three</small><strong>The Forever Inside Nothing</strong></span>
            <em>Coming soon <span aria-hidden="true">→</span></em>
          </Link>
        </div>
      </section>

      <section className="author-teaser section-pad" aria-labelledby="author-teaser-title">
        <div className="author-teaser__image-wrap">
          <img src="/about/author-r-a-hewitt.webp" alt="R A Hewitt" width="740" height="1110" loading="lazy" decoding="async" />
        </div>
        <div className="author-teaser__copy">
          <p className="eyebrow">About the author</p>
          <h2 id="author-teaser-title">Systems thinking, turned into fiction.</h2>
          <p>
            R A Hewitt is a British CTO, programmer, systems architect, and former graphic designer. His fiction brings together a lifelong fascination with computing, visual structure, and science fiction.
          </p>
          <div className="author-teaser__links">
            <Link className="text-link" to="/about">Meet the author <span aria-hidden="true">→</span></Link>
            <SocialLink className="text-link" source="homepage-author">Follow on X <span aria-hidden="true">↗</span></SocialLink>
            <GoodreadsLink className="text-link" source="homepage-author">Goodreads</GoodreadsLink>
          </div>
        </div>
      </section>


    </div>
  );
}
